/**
 * Business Partner Application form — client-side behavior.
 *
 * This file is intentionally generic: it doesn't hardcode field names.
 * It reads whatever fields the Liquid section rendered (driven by the
 * merchant's theme-editor blocks) and works with any of them.
 *
 * Responsibilities:
 *   - progress rail
 *   - "Other" text input show/hide for radio & checkbox groups
 *   - validation (native HTML5 + manual check for required checkbox groups)
 *   - build a flat { fieldKey: value } payload
 *   - submit via fetch() to the Google Apps Script Web App
 *   - loading state, success state, error state
 *   - prevent duplicate submissions
 */
(function () {
  'use strict';

  function init(formId, railId) {
    var form = document.getElementById(formId);
    if (!form || form.dataset.bpfInitialized) return;
    form.dataset.bpfInitialized = 'true';

    var wrap = form.closest('.bpf-wrap');
    var rail = railId ? document.getElementById(railId) : null;
    var steps = Array.prototype.slice.call(form.querySelectorAll('.bpf-step'));
    var submitBtn = form.querySelector('.bpf-submit-btn');
    var formErrorEl = form.querySelector('[data-form-error]');
    var confirmEl = wrap.querySelector('[data-confirm]');
    var endpoint = form.dataset.endpoint;
    var isSubmitting = false; // duplicate-submission guard
    var hasSubmittedOnce = false;

    // ---- Progress rail -----------------------------------------------
    if (rail) {
      steps.forEach(function () {
        var seg = document.createElement('div');
        seg.className = 'bpf-rail-seg';
        rail.appendChild(seg);
      });
    }

    function updateRail() {
      if (!rail) return;
      var segs = rail.querySelectorAll('.bpf-rail-seg');
      steps.forEach(function (step, i) {
        segs[i].classList.toggle('done', isStepFilled(step));
      });
    }

    function isStepFilled(step) {
      var ok = true;
      step.querySelectorAll('input[required], textarea[required], select[required]').forEach(function (el) {
        if (el.type === 'radio' || el.type === 'checkbox') return; // handled via groups below
        if (!el.value || !el.value.trim()) ok = false;
      });
      step.querySelectorAll('[data-group-required="true"]').forEach(function (group) {
        var key = group.dataset.groupKey;
        var checked = form.querySelectorAll('input[data-field-key="' + key + '"]:checked');
        if (!checked.length) ok = false;
      });
      return ok;
    }

    form.addEventListener('input', updateRail);
    form.addEventListener('change', updateRail);
    updateRail();

    // ---- "Other" toggle (generic, works for any radio/checkbox group) -
    form.querySelectorAll('[data-pill-group]').forEach(function (group) {
      var key = group.dataset.pillGroup;
      var otherWrap = form.querySelector('[data-other-for="' + key + '"]');
      if (!otherWrap) return;

      group.addEventListener('change', function () {
        var triggerInputs = group.querySelectorAll('[data-triggers-other="true"]');
        var anyChecked = Array.prototype.some.call(triggerInputs, function (el) { return el.checked; });
        otherWrap.style.display = anyChecked ? 'block' : 'none';
      });
    });

    // ---- Validation -----------------------------------------------------
    function validate() {
      var valid = form.checkValidity();
      form.querySelectorAll('input, textarea, select').forEach(function (el) {
        el.classList.toggle('bpf-invalid', !el.checkValidity());
      });

      // Required checkbox groups: HTML5 can't express "at least one of these
      // checkboxes", so check manually.
      form.querySelectorAll('[data-group-type="checkbox_group"][data-group-required="true"]').forEach(function (group) {
        var key = group.dataset.groupKey;
        var checked = form.querySelectorAll('input[data-field-key="' + key + '"]:checked');
        var errorEl = form.querySelector('[data-error-for="' + key + '"]');
        var groupValid = checked.length > 0;
        if (errorEl) errorEl.classList.toggle('show', !groupValid);
        if (!groupValid) valid = false;
      });

      return valid;
    }

    // ---- Build payload ----------------------------------------------
    function otherValueFor(key, rawValue) {
      if (rawValue !== 'Other') return rawValue;
      var otherInput = form.querySelector('[data-other-input-for="' + key + '"]');
      return (otherInput && otherInput.value.trim()) || 'Other';
    }

    function buildPayload() {
      var payload = {};
      var seenKeys = {};

      form.querySelectorAll('[data-field-key]').forEach(function (el) {
        seenKeys[el.dataset.fieldKey] = true;
      });

      Object.keys(seenKeys).forEach(function (key) {
        var fields = form.querySelectorAll('[data-field-key="' + key + '"]');
        var first = fields[0];

        if (first.type === 'checkbox') {
          var values = [];
          fields.forEach(function (el) {
            if (el.checked) values.push(otherValueFor(key, el.value));
          });
          payload[key] = values.join(', ');
        } else if (first.type === 'radio') {
          var checkedEl = Array.prototype.find.call(fields, function (el) { return el.checked; });
          payload[key] = checkedEl ? otherValueFor(key, checkedEl.value) : '';
        } else {
          payload[key] = first.value.trim();
        }
      });

      return payload;
    }

    // ---- Submit -------------------------------------------------------
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      if (isSubmitting) return; // duplicate-submission guard
      if (hasSubmittedOnce) return; // form already succeeded once this page load

      hideFormError();

      if (!validate()) {
        var firstInvalid = form.querySelector('.bpf-invalid, .bpf-error-msg.show');
        if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (!endpoint) {
        showFormError('This form is not connected yet. Add the Google Apps Script Web App URL in the section settings.');
        return;
      }

      var payload = buildPayload();
      setLoading(true);
      isSubmitting = true;

      // Sent as text/plain (not application/json) on purpose: it keeps this
      // a CORS "simple request", so the browser skips a preflight OPTIONS
      // call — which Google Apps Script Web Apps don't answer reliably.
      // Code.gs still parses the body as JSON on the server side.
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Server responded with ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (!data || data.result !== 'success') {
            throw new Error((data && data.message) || 'Unknown error from the server.');
          }
          hasSubmittedOnce = true;
          showSuccess();
        })
        .catch(function (err) {
          showFormError('We couldn\'t submit your application. ' + (err.message || 'Please check your connection and try again.'));
        })
        .finally(function () {
          setLoading(false);
          isSubmitting = false;
        });
    });

    function setLoading(isLoading) {
      submitBtn.disabled = isLoading;
      submitBtn.classList.toggle('is-loading', isLoading);
    }

    function showFormError(message) {
      if (!formErrorEl) return;
      formErrorEl.textContent = message;
      formErrorEl.style.display = 'block';
    }

    function hideFormError() {
      if (!formErrorEl) return;
      formErrorEl.style.display = 'none';
      formErrorEl.textContent = '';
    }

    function showSuccess() {
      form.style.display = 'none';
      var hero = wrap.querySelector('.bpf-hero');
      if (hero) hero.style.display = 'none';
      if (confirmEl) confirmEl.classList.add('show');
    }
  }

  window.BusinessPartnerForm = { init: init };
})();